<template>
  <div class="space-y-4">
    <div>
      <p class="text-sm font-semibold text-black">Photos par couleur</p>
      <p class="text-xs text-gray-500 mt-1">
        Une image liée à plusieurs couleurs, ou la photo du produit sans
        couleur précise, apparaît sous chacune d'elles.
      </p>
    </div>
    <div
      v-if="loading"
      class="flex h-28 items-center justify-center text-gray-500"
    >
      <i class="fas fa-spinner fa-spin mr-2"></i>
      Chargement des photos…
    </div>
    <template v-else>
      <p
        v-if="!selectedColors?.length"
        class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
      >
        Sélectionnez au moins une couleur pour pouvoir associer les photos.
      </p>
      <p
        v-else-if="!localPhotos.length"
        class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
      >
        Aucune photo associée à une couleur. Ajoutez au moins une image par
        couleur — obligatoire pour mettre le produit en ligne sur eFashion.
      </p>
      <section
        v-for="section in photoSections"
        :key="section.key"
        class="border border-gray-200 rounded-lg p-3 space-y-3 bg-white"
        @dragover.prevent
        @drop.prevent="assignDraggedPhotoToSection($event, section)"
      >
        <div
          v-if="section.key !== 'all'"
          class="flex items-center justify-between gap-2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              v-if="section.hexColor || section.colorId"
              class="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0"
              :style="{ backgroundColor: section.hexColor || '#CCCCCC' }"
            ></span>
            <h3 class="text-sm font-medium text-gray-900 truncate">
              {{ section.label }}
              <span v-if="section.caption" class="text-gray-500 font-normal">
                · {{ section.caption }}
              </span>
              <span class="text-gray-500 font-normal">
                ({{ section.entries.length }})
              </span>
            </h3>
          </div>
        </div>
        <div
          v-if="section.entries.length"
          class="flex gap-3 overflow-x-auto pb-1"
        >
          <div
            v-for="entry in section.entries"
            :key="entry.photo.key"
            class="relative group flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
            :class="[
              draggingIndex === entry.index ? 'opacity-50 scale-95' : '',
              dragOverIndex === entry.index
                ? 'ring-2 ring-blue-400 ring-offset-1'
                : '',
              entry.index === 0 ? 'border-2 border-blue-500' : '',
            ]"
            :draggable="!persisting"
            @dragstart="handleDragStart($event, entry.index)"
            @dragend="handleDragEnd"
            @dragover="handleDragOver($event, entry.index)"
            @drop.stop="handleDrop($event, entry.index, section)"
          >
            <img
              :src="entry.photo.previewUrl"
              :alt="`Photo ${entry.index + 1}`"
              class="w-full h-full object-contain pointer-events-none transition-transform duration-200"
              :style="{ transform: `rotate(${entry.photo.rotation}deg)` }"
            />
            <div
              v-if="entry.index === 0"
              class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight pointer-events-none"
            >
              Couverture
            </div>
            <div
              class="absolute inset-0 bg-black/50 transition-opacity grid grid-cols-2 gap-1 items-center justify-center content-center p-1 opacity-100 pointer-events-auto md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto"
            >
              <button
                v-if="entry.index !== 0"
                type="button"
                class="p-2 bg-white/90 text-yellow-500 rounded-full hover:bg-white disabled:opacity-60"
                title="Définir comme photo de couverture"
                :disabled="persisting"
                @click.stop="setCover(entry.index)"
              >
                <i class="fas fa-star text-sm"></i>
              </button>
              <button
                type="button"
                class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white disabled:opacity-60"
                title="Pivoter la photo"
                :disabled="persisting"
                @click.stop="rotatePhoto(entry.index)"
              >
                <i class="fas fa-redo-alt text-sm"></i>
              </button>
              <button
                v-if="localPhotos.length > 1"
                type="button"
                class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white disabled:opacity-60"
                title="Supprimer la photo"
                :disabled="persisting"
                @click.stop="requestDelete(entry.index)"
              >
                <i class="fas fa-trash-alt text-sm"></i>
              </button>
            </div>
          </div>
        </div>
        <p v-else-if="section.key !== 'all'" class="text-xs text-gray-500">
          Aucune photo pour cette couleur.
        </p>
        <div
          :class="[
            'border-2 border-dashed rounded-lg p-3 text-center transition-all duration-200',
            isDragOverUpload && uploadSectionKey === section.key
              ? 'border-blue-500 bg-blue-50 shadow-md cursor-grabbing'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer bg-white',
            persisting || localPhotos.length >= maxPhotos
              ? 'opacity-60 pointer-events-none'
              : '',
          ]"
          @click="triggerFileDialog(section)"
          @dragenter.prevent="
            isDragOverUpload = true;
            uploadSectionKey = section.key;
          "
          @dragover.prevent="
            isDragOverUpload = true;
            uploadSectionKey = section.key;
          "
          @dragleave.prevent="
            isDragOverUpload = false;
            uploadSectionKey = null;
          "
          @drop.prevent="handleUploadDrop($event, section)"
        >
          <div
            class="flex flex-col items-center gap-2 justify-center min-h-[60px]"
          >
            <i
              class="fas fa-cloud-upload-alt text-2xl"
              :class="
                isDragOverUpload && uploadSectionKey === section.key
                  ? 'text-blue-500 scale-110'
                  : 'text-black'
              "
            ></i>
            <span
              v-if="uploading && uploadSectionKey === section.key"
              class="flex items-center gap-2 text-xs text-blue-600 font-medium"
            >
              <span
                class="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
              ></span>
              Téléversement en cours…
            </span>
            <span v-else class="text-xs text-gray-600">
              Ajouter des photos — {{ section.label }}
            </span>
          </div>
        </div>
      </section>
      <p
        v-if="localPhotos.length || selectedColors?.length"
        class="text-xs text-gray-500"
      >
        Chaque couleur sélectionnée doit avoir au moins une photo. La couverture
        est la première photo de la liste globale.
      </p>
      <input
        ref="fileInputRef"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        class="hidden"
        multiple
        @change="handleFileInput"
      />
    </template>

    <Teleport to="body">
      <ConfirmModal
        :show="showDeleteModal"
        title="Supprimer la photo"
        message="Êtes-vous sûr de vouloir supprimer cette photo ?"
        confirm-text="Supprimer"
        :lock-background-scroll="false"
        max-width-class="max-w-md"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import {
  getPrestashopCustomImageUrl,
  getPrestashopProductImage,
  savePrestashopProductImages,
  uploadPrestashopCustomImage,
} from "@/api/prestashop";
import type { PrestashopImageOverride } from "@/types/prestashop";

type LocalPhoto = {
  key: string;
  type: "prestashop" | "custom";
  prestashopImageId?: number;
  customFile?: string;
  url?: string;
  previewUrl: string;
  rotation: number;
  revokeOnDestroy: boolean;
  colorId: number | null;
  colorName: string | null;
  colorIds: number[];
  colorNames: string[];
  shared: boolean;
};

type ColorOption = {
  id: number;
  nom: string;
  isMain?: boolean;
  hexColor?: string | null;
};

type PhotoSection = {
  key: string;
  label: string;
  caption: string;
  hexColor: string | null;
  colorId: number | null;
  colorName: string | null;
  entries: Array<{ photo: LocalPhoto; index: number }>;
};

const props = defineProps<{
  productId: number;
  sourceImageIds: number[];
  savedImages?: PrestashopImageOverride[];
  selectedColors?: ColorOption[];
  colorCollisions?: Array<{
    colorId: number;
    officialLabel: string;
    names: string[];
  }>;
  colorSourceChoices?: Array<{ sourceName: string; colorId: number }>;
  linkedSourceLabelsByColorId?: Record<number, string[]>;
  sourceColorByImageId?: Record<number, string[]>;
}>();

const emit = defineEmits<{
  (event: "update:images", value: PrestashopImageOverride[]): void;
  (event: "saved", value: PrestashopImageOverride[]): void;
  (event: "error", message: string): void;
}>();

const maxPhotos = 20;
const allowedTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const loading = ref(false);
const uploading = ref(false);
const persisting = ref(false);
const localPhotos = ref<LocalPhoto[]>([]);
const isDragOverUpload = ref(false);
const uploadSectionKey = ref<string | null>(null);
const pendingUploadColorId = ref<number | null>(null);
const pendingUploadColorName = ref<string | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const draggingIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);
const showDeleteModal = ref(false);
const deleteIndex = ref<number | null>(null);
const lastPersistedOverrides = ref<PrestashopImageOverride[]>([]);
let loadToken = 0;

function normalizeOverrides(
  images?: PrestashopImageOverride[]
): PrestashopImageOverride[] {
  if (!images?.length) return [];
  return images.map((image) => ({
    type: image.type,
    id: image.id,
    file: image.file,
    url: image.url,
    rotation: image.rotation ?? 0,
    colorId: image.colorId ?? null,
    colorName: image.colorName ?? null,
    colorIds: image.colorIds ?? [],
    colorNames: image.colorNames ?? [],
    shared: Boolean(image.shared),
  }));
}

function normalizeCollisionLabel(value: string | null | undefined) {
  return (
    value
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("fr")
      .replace(/[^a-z0-9]+/g, " ")
      .trim() ?? ""
  );
}

function collidingNames() {
  return (props.colorCollisions ?? []).flatMap((group) => group.names);
}

function isCollidingSourceName(name: string | null | undefined) {
  const key = normalizeCollisionLabel(name);
  if (!key) return false;
  return collidingNames().some(
    (label) => normalizeCollisionLabel(label) === key
  );
}

function choiceForSourceName(name: string | null | undefined) {
  const key = normalizeCollisionLabel(name);
  if (!key) return null;
  return (
    (props.colorSourceChoices ?? []).find(
      (choice) => normalizeCollisionLabel(choice.sourceName) === key
    ) ?? null
  );
}

function isUnresolvedCollision(name: string | null | undefined) {
  return isCollidingSourceName(name) && !choiceForSourceName(name);
}

function collisionColorIds() {
  return new Set((props.colorCollisions ?? []).map((group) => group.colorId));
}

function chosenColorIds() {
  return new Set(
    (props.colorSourceChoices ?? []).map((choice) => choice.colorId)
  );
}

function isAbandonedMappedColor(colorId: number) {
  const labels = props.linkedSourceLabelsByColorId?.[colorId] ?? [];
  if (labels.length < 2) return false;
  return labels.every((label) => {
    const choice = choiceForSourceName(label);
    return Boolean(choice && choice.colorId !== colorId);
  });
}

function linkedCaptionFor(colorId: number | null) {
  if (!colorId) return "";
  const sheetLabels = (props.colorSourceChoices ?? [])
    .filter((choice) => choice.colorId === colorId)
    .map((choice) => choice.sourceName);
  if (sheetLabels.length) return `liée : ${sheetLabels.join(", ")}`;
  if (collisionColorIds().has(colorId)) return "";
  const labels = props.linkedSourceLabelsByColorId?.[colorId] ?? [];
  if (!labels.length) return "";
  return `liée : ${labels.join(", ")}`;
}

function normalizeColorLabel(value: string | null | undefined) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, "");
}

const colorAliases: Record<string, string[]> = {
  white: ["blanc"],
  black: ["noir"],
  gray: ["gris"],
  grey: ["gris"],
  red: ["rouge"],
  blue: ["bleu"],
  green: ["vert"],
  yellow: ["jaune"],
  brown: ["marron"],
  pink: ["rose"],
  purple: ["violet"],
  navy: ["bleumarine", "marine"],
  offwhite: ["blanccasse"],
  gold: ["dore", "or"],
  silver: ["argente", "argent"],
  blanc: ["white"],
  noir: ["black"],
  gris: ["gray", "grey"],
  rouge: ["red"],
  bleu: ["blue"],
  vert: ["green"],
  jaune: ["yellow"],
  marron: ["brown"],
  rose: ["pink"],
  violet: ["purple"],
  blanccasse: ["offwhite"],
  bleumarine: ["navy", "marine"],
  marine: ["navy", "bleumarine"],
};

function equivalentColorLabels(label: string) {
  const compact = normalizeColorLabel(label);
  if (!compact) return [];
  return [compact, ...(colorAliases[compact] ?? [])];
}

function officialColorForSourceLabel(label: string): ColorOption | null {
  const choice = choiceForSourceName(label);
  if (choice) {
    return (
      (props.selectedColors ?? []).find((color) => color.id === choice.colorId) ??
      null
    );
  }
  if (isCollidingSourceName(label)) return null;
  const needles = new Set(equivalentColorLabels(label));
  if (!needles.size) return null;
  const colors = props.selectedColors ?? [];
  return (
    colors.find((color) =>
      equivalentColorLabels(color.nom).some((key) => needles.has(key))
    ) ??
    colors.find((color) =>
      (props.linkedSourceLabelsByColorId?.[color.id] ?? []).some(
        (sourceLabel) =>
          equivalentColorLabels(sourceLabel).some((key) => needles.has(key))
      )
    ) ??
    null
  );
}

function sourceLabelsFor(photo: LocalPhoto): string[] {
  const fromMap = photo.prestashopImageId
    ? props.sourceColorByImageId?.[photo.prestashopImageId] ?? []
    : [];
  if (fromMap.length) return fromMap;
  return Array.from(
    new Set([
      ...(photo.colorName?.trim() ? [photo.colorName.trim()] : []),
      ...photo.colorNames.map((name) => name.trim()).filter(Boolean),
    ])
  );
}

function labelMatchesColor(label: string, color: ColorOption) {
  return officialColorForSourceLabel(label)?.id === color.id;
}

function linkedColorIds(photo: LocalPhoto): number[] {
  const explicit = new Set<number>();
  if (photo.colorId) explicit.add(photo.colorId);
  for (const id of photo.colorIds) {
    if (id) explicit.add(id);
  }
  if (explicit.size) return [...explicit];
  const fromLabels = new Set<number>();
  for (const label of sourceLabelsFor(photo)) {
    if (isUnresolvedCollision(label)) continue;
    const official = officialColorForSourceLabel(label);
    if (official) fromLabels.add(official.id);
  }
  return [...fromLabels];
}

function photoMatchesColor(photo: LocalPhoto, color: ColorOption) {
  if (sourceLabelsFor(photo).some((label) => isUnresolvedCollision(label))) {
    return false;
  }
  const ids = linkedColorIds(photo);
  if (ids.length) return ids.includes(color.id);
  return Boolean(photo.shared);
}

function photoRepeatsAcrossColors(photo: LocalPhoto) {
  if (linkedColorIds(photo).length > 1) return true;
  return Boolean(photo.shared) && linkedColorIds(photo).length === 0;
}

const photoSections = computed<PhotoSection[]>(() => {
  const indexed = localPhotos.value.map((photo, index) => ({ photo, index }));
  const placed = new Set<string>();
  const reservedByCollision = new Set<string>();
  const sections: PhotoSection[] = [];
  for (const name of collidingNames()) {
    if (choiceForSourceName(name)) continue;
    const entries = indexed.filter(({ photo }) =>
      sourceLabelsFor(photo).some(
        (label) => normalizeCollisionLabel(label) === normalizeCollisionLabel(name)
      )
    );
    entries.forEach(({ photo }) => {
      placed.add(photo.key);
      reservedByCollision.add(photo.key);
    });
    sections.push({
      key: `source-${normalizeCollisionLabel(name)}`,
      label: name,
      caption: "Couleur à séparer",
      hexColor: null,
      colorId: null,
      colorName: name,
      entries,
    });
  }
  const colors = (props.selectedColors ?? []).filter((color) => {
    if (isAbandonedMappedColor(color.id)) return false;
    if (collisionColorIds().has(color.id) && !chosenColorIds().has(color.id)) {
      return false;
    }
    return true;
  });
  sections.push(
    ...colors.map((color) => {
    const entries = indexed.filter(({ photo }) => {
      if (reservedByCollision.has(photo.key)) return false;
      if (!photoMatchesColor(photo, color)) return false;
      return photoRepeatsAcrossColors(photo) || !placed.has(photo.key);
    });
    entries.forEach(({ photo }) => {
      if (!photoRepeatsAcrossColors(photo)) placed.add(photo.key);
    });
    return {
      key: `color-${color.id}`,
      label: color.nom,
      caption: linkedCaptionFor(color.id),
      hexColor: color.hexColor || null,
      colorId: color.id,
      colorName: color.nom,
      entries,
    };
  }),
  );
  for (const section of sections) {
    section.entries.forEach(({ photo }) => placed.add(photo.key));
  }

  const sourceGroups = new Map<string, PhotoSection["entries"]>();
  for (const entry of indexed) {
    if (placed.has(entry.photo.key)) continue;
    const label = entry.photo.colorName?.trim();
    if (!label || officialColorForSourceLabel(label)) continue;
    const key = normalizeColorLabel(label);
    const group = sourceGroups.get(key) ?? [];
    group.push(entry);
    sourceGroups.set(key, group);
    placed.add(entry.photo.key);
  }
  for (const [key, entries] of sourceGroups) {
    sections.push({
      key: `source-${key}`,
      label: entries[0]?.photo.colorName?.trim() || "Couleur PrestaShop",
      caption: "Couleur PrestaShop, pas encore liée",
      hexColor: null,
      colorId: null,
      colorName: entries[0]?.photo.colorName ?? null,
      entries,
    });
  }

  return sections;
});

function assignDraggedPhotoToSection(event: DragEvent, section: PhotoSection) {
  if (event.dataTransfer?.files?.length) return;
  const sourceIndex = draggingIndex.value;
  draggingIndex.value = null;
  dragOverIndex.value = null;
  if (sourceIndex == null || !section.colorId) return;
  const photo = localPhotos.value[sourceIndex];
  if (!photo || photo.colorId === section.colorId) return;
  photo.colorId = section.colorId;
  photo.colorName = section.colorName;
  photo.colorIds = [section.colorId];
  photo.colorNames = section.colorName ? [section.colorName] : [];
  photo.shared = false;
  void persistPhotos().catch(() => {
    void loadPhotosFromOverrides(lastPersistedOverrides.value);
  });
}

let sourceSplitApplied = false;

function applySourceColorLinks() {
  const source = props.sourceColorByImageId ?? {};
  const linked = localPhotos.value.filter(
    (photo) => photo.prestashopImageId && source[photo.prestashopImageId]
  );
  const sourceLabels = new Set(
    linked.flatMap((photo) => source[photo.prestashopImageId as number] ?? [])
  );
  const currentIds = new Set(
    linked
      .map((photo) => photo.colorId)
      .filter((id): id is number => Boolean(id))
  );
  const forceSplit =
    !sourceSplitApplied && sourceLabels.size >= 2 && currentIds.size <= 1;
  if (forceSplit) sourceSplitApplied = true;

  let changed = false;
  const selectedIds = new Set(
    (props.selectedColors ?? []).map((color) => color.id)
  );
  for (const photo of localPhotos.value) {
    const imageId = photo.prestashopImageId;
    const labels = imageId ? source[imageId] ?? [] : [];
    if (
      photo.colorId &&
      selectedIds.size > 0 &&
      !selectedIds.has(photo.colorId)
    ) {
      photo.colorId = null;
      photo.colorName = labels[0] ?? null;
      changed = true;
      continue;
    }
    if (labels.length > 1) {
      if (forceSplit && photo.colorId) {
        photo.colorId = null;
        photo.colorName = null;
        changed = true;
      }
      continue;
    }
    const label = labels[0];
    if (!label) {
      const match = (props.selectedColors ?? []).find(
        (color) => color.id === photo.colorId
      );
      if (match && photo.colorName !== match.nom) {
        photo.colorName = match.nom;
        changed = true;
      }
      continue;
    }
    if (photo.colorId && !forceSplit) continue;
    const official = officialColorForSourceLabel(label);
    const nextId = official?.id ?? null;
    const nextName = official?.nom ?? label;
    if (photo.colorId !== nextId || photo.colorName !== nextName) {
      photo.colorId = nextId;
      photo.colorName = nextName;
      changed = true;
    }
  }
  if (changed) emit("update:images", toOverrides(localPhotos.value));
}

function buildInitialOverrides(): PrestashopImageOverride[] {
  const saved = normalizeOverrides(props.savedImages);
  if (saved.length) return saved;
  return props.sourceImageIds.map((id) => ({
    type: "prestashop" as const,
    id,
    rotation: 0,
    shared: true,
  }));
}

function overridesEqual(
  left: PrestashopImageOverride[],
  right: PrestashopImageOverride[]
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function toOverrides(photos: LocalPhoto[]): PrestashopImageOverride[] {
  return photos.map((photo) => ({
    type: photo.type,
    id: photo.prestashopImageId,
    file:
      photo.type === "custom"
        ? photo.customFile
        : photo.url
        ? `ps-${photo.prestashopImageId}.jpg`
        : undefined,
    url: photo.url,
    rotation: photo.rotation,
    ...(photo.colorId ? { colorId: photo.colorId } : {}),
    ...(photo.colorName ? { colorName: photo.colorName } : {}),
    ...(photo.colorIds.length ? { colorIds: photo.colorIds } : {}),
    ...(photo.colorNames.length ? { colorNames: photo.colorNames } : {}),
    ...(photo.shared ? { shared: true } : {}),
  }));
}

function revokePreviews() {
  for (const photo of localPhotos.value) {
    if (photo.revokeOnDestroy) URL.revokeObjectURL(photo.previewUrl);
  }
  localPhotos.value = [];
}

async function loadPreview(
  override: PrestashopImageOverride
): Promise<LocalPhoto | null> {
  const rotation = override.rotation ?? 0;
  if (override.url) {
    return {
      key: override.file || `${override.type}-${override.id || override.url}`,
      type: override.type === "custom" ? "custom" : "prestashop",
      prestashopImageId:
        override.type === "prestashop" ? Number(override.id) : undefined,
      customFile: override.type === "custom" ? override.file : undefined,
      url: override.url,
      previewUrl: override.url,
      rotation,
      revokeOnDestroy: false,
      colorId: override.colorId ?? null,
      colorName: override.colorName ?? null,
      colorIds: override.colorIds ?? [],
      colorNames: override.colorNames ?? [],
      shared: Boolean(override.shared),
    };
  }
  if (override.type === "prestashop" && override.id) {
    try {
      const blob = await getPrestashopProductImage(
        props.productId,
        override.id
      );
      return {
        key: `ps-${override.id}`,
        type: "prestashop",
        prestashopImageId: override.id,
        previewUrl: URL.createObjectURL(blob),
        rotation,
        revokeOnDestroy: true,
        colorId: override.colorId ?? null,
        colorName: override.colorName ?? null,
        colorIds: override.colorIds ?? [],
        colorNames: override.colorNames ?? [],
        shared: Boolean(override.shared),
      };
    } catch {
      return null;
    }
  }
  if (override.type === "custom" && override.file) {
    return {
      key: `custom-${override.file}`,
      type: "custom",
      customFile: override.file,
      previewUrl: getPrestashopCustomImageUrl(props.productId, override.file),
      rotation,
      revokeOnDestroy: false,
      colorId: override.colorId ?? null,
      colorName: override.colorName ?? null,
      colorIds: override.colorIds ?? [],
      colorNames: override.colorNames ?? [],
      shared: Boolean(override.shared),
    };
  }
  return null;
}

async function loadPhotosFromOverrides(overrides: PrestashopImageOverride[]) {
  const token = ++loadToken;
  loading.value = true;
  revokePreviews();
  try {
    const results = await Promise.all(
      overrides.map((item) => loadPreview(item))
    );
    if (token !== loadToken) {
      results
        .filter((item): item is LocalPhoto => Boolean(item?.revokeOnDestroy))
        .forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return;
    }
    localPhotos.value = results.filter((item): item is LocalPhoto =>
      Boolean(item)
    );
    applySourceColorLinks();
    lastPersistedOverrides.value = toOverrides(localPhotos.value);
  } finally {
    if (token === loadToken) loading.value = false;
  }
}

async function loadPhotos() {
  await loadPhotosFromOverrides(buildInitialOverrides());
  if (localPhotos.value.some((photo) => !photo.url)) {
    await persistPhotos(true).catch(() => undefined);
  }
}

async function persistPhotos(force = false) {
  const images = toOverrides(localPhotos.value);
  if (!force && overridesEqual(images, lastPersistedOverrides.value)) {
    emit("update:images", images);
    return;
  }
  persisting.value = true;
  try {
    const response = await savePrestashopProductImages(props.productId, images);
    const savedImages = normalizeOverrides(response.images);
    lastPersistedOverrides.value = savedImages;
    await loadPhotosFromOverrides(savedImages);
    emit("update:images", savedImages);
    emit("saved", savedImages);
  } catch (caught) {
    emit(
      "error",
      caught instanceof Error
        ? caught.message
        : "Sauvegarde des photos impossible."
    );
    throw caught;
  } finally {
    persisting.value = false;
  }
}

function triggerFileDialog(section?: PhotoSection) {
  pendingUploadColorId.value = section?.colorId ?? null;
  pendingUploadColorName.value = section?.colorName ?? null;
  uploadSectionKey.value = section?.key ?? null;
  fileInputRef.value?.click();
}

async function addFiles(files: FileList | File[]) {
  const candidates = Array.from(files).filter((file) =>
    allowedTypes.has(file.type.toLowerCase())
  );
  if (!candidates.length) {
    emit("error", "Format non autorisé. Utilisez JPG, PNG, WEBP ou GIF.");
    return;
  }
  const remaining = maxPhotos - localPhotos.value.length;
  if (remaining <= 0) {
    emit("error", `Nombre maximum de photos atteint (${maxPhotos}).`);
    return;
  }
  uploading.value = true;
  try {
    for (const file of candidates.slice(0, remaining)) {
      const uploaded = await uploadPrestashopCustomImage(props.productId, file);
      localPhotos.value.push({
        key: `custom-${uploaded.file}`,
        type: "custom",
        customFile: uploaded.file,
        url: uploaded.url,
        previewUrl: uploaded.url,
        rotation: 0,
        revokeOnDestroy: false,
        colorId: pendingUploadColorId.value,
        colorName: pendingUploadColorName.value,
        colorIds: pendingUploadColorId.value
          ? [pendingUploadColorId.value]
          : [],
        colorNames: pendingUploadColorName.value
          ? [pendingUploadColorName.value]
          : [],
        shared: !pendingUploadColorId.value,
      });
    }
    await persistPhotos();
  } catch (caught) {
    if (!(caught instanceof Error)) {
      emit("error", "Ajout de photo impossible.");
    }
  } finally {
    uploading.value = false;
  }
}

function handleFileInput(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) void addFiles(input.files);
  input.value = "";
}

function handleUploadDrop(event: DragEvent, section?: PhotoSection) {
  isDragOverUpload.value = false;
  uploadSectionKey.value = section?.key ?? null;
  pendingUploadColorId.value = section?.colorId ?? null;
  pendingUploadColorName.value = section?.colorName ?? null;
  if (event.dataTransfer?.files?.length) {
    void addFiles(event.dataTransfer.files);
  }
}

async function rotatePhoto(index: number) {
  const photo = localPhotos.value[index];
  if (!photo) return;
  photo.rotation = (photo.rotation + 90) % 360;
  try {
    await persistPhotos();
  } catch {
    photo.rotation = (photo.rotation + 270) % 360;
  }
}

async function setCover(index: number) {
  if (index <= 0 || index >= localPhotos.value.length) return;
  const photos = [...localPhotos.value];
  const [selected] = photos.splice(index, 1);
  photos.unshift(selected);
  localPhotos.value = photos;
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

function requestDelete(index: number) {
  if (localPhotos.value.length <= 1) {
    emit(
      "error",
      "Impossible de supprimer la dernière photo : au moins une image est requise pour la mise en ligne."
    );
    return;
  }
  deleteIndex.value = index;
  showDeleteModal.value = true;
}

function cancelDelete() {
  showDeleteModal.value = false;
  deleteIndex.value = null;
}

async function confirmDelete() {
  const index = deleteIndex.value;
  showDeleteModal.value = false;
  deleteIndex.value = null;
  if (index == null) return;
  const [removed] = localPhotos.value.splice(index, 1);
  if (removed?.revokeOnDestroy) URL.revokeObjectURL(removed.previewUrl);
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

function handleDragStart(event: DragEvent, index: number) {
  draggingIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.setData("text/plain", String(index));
    event.dataTransfer.effectAllowed = "move";
  }
}

function handleDragEnd() {
  draggingIndex.value = null;
  dragOverIndex.value = null;
}

function handleDragOver(event: DragEvent, index: number) {
  event.preventDefault();
  dragOverIndex.value = index;
}

async function handleDrop(
  event: DragEvent,
  targetIndex: number,
  section?: PhotoSection
) {
  event.preventDefault();
  const sourceIndex = draggingIndex.value;
  draggingIndex.value = null;
  dragOverIndex.value = null;
  if (sourceIndex == null || sourceIndex === targetIndex) return;
  const photos = [...localPhotos.value];
  const [moved] = photos.splice(sourceIndex, 1);
  if (section?.colorId) {
    moved.colorId = section.colorId;
    moved.colorName = section.colorName;
  }
  photos.splice(targetIndex, 0, moved);
  localPhotos.value = photos;
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

watch(
  () => [props.productId, props.sourceImageIds.join(",")] as const,
  () => {
    sourceSplitApplied = false;
    void loadPhotos();
  },
  { immediate: true }
);

watch(
  () =>
    [
      (props.selectedColors ?? [])
        .map((color) => `${color.id}:${color.nom}`)
        .join(","),
      Object.entries(props.sourceColorByImageId ?? {})
        .map(([id, labels]) => `${id}:${labels.join("|")}`)
        .join(","),
      Object.entries(props.linkedSourceLabelsByColorId ?? {})
        .map(([id, labels]) => `${id}:${labels.join("|")}`)
        .join(","),
      (props.colorSourceChoices ?? [])
        .map((choice) => `${choice.sourceName}:${choice.colorId}`)
        .join(","),
      (props.colorCollisions ?? [])
        .map((group) => `${group.colorId}:${group.names.join("|")}`)
        .join(","),
    ].join("||"),
  () => {
    if (loading.value) return;
    applySourceColorLinks();
  }
);

onUnmounted(() => {
  loadToken += 1;
  revokePreviews();
});

defineExpose({
  photoCount: () => localPhotos.value.length,
});
</script>
